import { CommonModule } from "@angular/common";
import { Component, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25LoadingInlineModule } from "../../modules/s25-loading/s25.loading.inline.module";
import { S25ToggleButtonModule } from "../../modules/s25-toggle-button/s25.toggle.button.module";
import { S25MultiselectModule } from "../../modules/s25-multiselect/s25-multiselect.module";
import { Event } from "../../pojo/Event";
import { S25ItemI } from "../../pojo/S25ItemI";
import { ColorBucketType, ItemColor, ItemColorMappingService } from "../../services/item.color.mapping.service";
import { SearchCriteriaService } from "../../services/search/search-criteria/search.criteria.service";

type BucketTypeName = "Event Type" | "State";
type SearchCriteriaType = "eventTypes" | "eventStates";

interface ColorBucketVm {
    bucket_id: number | undefined;
    bucket_name: string;
    type: BucketTypeName;
    color: ItemColor | undefined;
    items: S25ItemI[];
    is_new?: 0 | 1;
    is_removed?: boolean;
    chosenColorName?: string;
    colors: ItemColor[];
    selectedItems: S25ItemI[];
    criteriaType: SearchCriteriaType;
    allowedItemIds?: number[];
}

interface ColorBucketTypeVm {
    is_enabled: 0 | 1;
    type: BucketTypeName;
    buckets: ColorBucketVm[];
    bucketCount: number;
}

@TypeManagerDecorator("s25-ng-item-color-mapping")
@Component({
    selector: "s25-ng-item-color-mapping",
    standalone: true,
    imports: [CommonModule, FormsModule, S25LoadingInlineModule, S25ToggleButtonModule, S25MultiselectModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    template: `
        <div class="gridColoring">
            <h2 class="c-margin-bottom--single">Grid Coloring</h2>
            @if (!isInit()) {
                <s25-loading-inline [model]="{}"></s25-loading-inline>
            } @else {
                <div class="ngInline">
                    <p class="ngBold">Color By</p>
                    <s25-toggle-button
                        [value]="colorByBool()"
                        [falseLabel]="'Event Type'"
                        [trueLabel]="'Event State'"
                        (valueChange)="enableType($event)"
                    />
                </div>

                @for (bucketType of bucketTypes(); track bucketType.type) {
                    @if (bucketType.is_enabled === 1) {
                        <div>
                            <button
                                class="aw-button aw-button--outline c-margin-bottom--single"
                                (click)="addBucket(bucketType)"
                                [disabled]="bucketType.bucketCount >= colors().length"
                            >
                                Add New Color Mapping
                            </button>
                        </div>

                        @if (validationMessage() && !loading()) {
                            <div
                                [class]="
                                    (validationMessage() === 'Success!'
                                        ? 'ngGreen cn-alert--success'
                                        : 'ngRed cn-alert--danger') + ' ngBold cn-alert c-margin-bottom--single'
                                "
                                role="alert"
                            >
                                <div class="cn-alert__label">
                                    <span>{{ validationMessage() }}</span>
                                </div>
                            </div>
                        }

                        <div class="color-mapping--flex-wrapper">
                            @for (bucket of bucketType.buckets; track bucket.bucket_id ?? $index) {
                                @if (!bucket.is_removed) {
                                    <div class="grid-color-card--wrapper" style="max-width: 400px">
                                        <div>
                                            <div class="grid-color-card--flex-row">
                                                <label [for]="'colorMappingName' + $index">Name:</label>
                                                <input
                                                    [id]="'colorMappingName' + $index"
                                                    type="text"
                                                    name="name"
                                                    [(ngModel)]="bucket.bucket_name"
                                                    class="c-input"
                                                />
                                            </div>
                                            <div class="grid-color-card--content">
                                                <div class="grid-color-card--flex-row">
                                                    <label [for]="'colorMappingColor' + $index">Color:</label>
                                                    <select
                                                        [id]="'colorMappingColor' + $index"
                                                        class="cn-form__control"
                                                        [(ngModel)]="bucket.chosenColorName"
                                                        (ngModelChange)="pickColor(bucketType, bucket)"
                                                        [attr.style]="selectStyle(bucket)"
                                                    >
                                                        @for (colorChoice of bucket.colors; track colorChoice.name) {
                                                            <option
                                                                [ngValue]="colorChoice.name"
                                                                [style.background-color]="colorChoice.hash"
                                                                [style.color]="colorChoice.textColor"
                                                            >
                                                                {{ colorChoice.name }}
                                                            </option>
                                                        }
                                                    </select>
                                                </div>
                                                <s25-ng-multiselect-search-criteria
                                                    [type]="bucket.criteriaType"
                                                    [allowedItemIds]="bucket.allowedItemIds"
                                                    [selectedItems]="bucket.selectedItems"
                                                    [value]="bucket.selectedItems"
                                                    [showResults]="true"
                                                    [buttonText]="bucket.type"
                                                    [popoverOnBody]="true"
                                                    (onDone)="setSelectedItems(bucketType, bucket, $event)"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <button
                                                class="aw-button aw-button--danger--outline c-margin-top--single"
                                                (click)="removeBucket(bucketType, bucket)"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                }
                            }
                        </div>

                        <div class="c-displayBlock c-padding-bottom--double">
                            <button class="aw-button aw-button--primary c-margin-top--half" (click)="save()">
                                Save
                            </button>
                        </div>
                    }
                }
            }
        </div>
    `,
})
export class ItemColorMappingComponent {
    readonly isInit = signal(false);
    readonly loading = signal(false);
    readonly validationMessage = signal<string | undefined>(undefined);
    readonly colors = signal<ItemColor[]>([]);
    readonly bucketTypes = signal<ColorBucketTypeVm[]>([]);
    readonly colorBy = signal<BucketTypeName>("Event Type");
    readonly colorByBool = signal(false);
    readonly allowedEventStateIds = signal<number[] | undefined>(undefined);

    private readonly excludedEventStateIds = new Set<number>([
        Event.State.Ids.Draft,
        Event.State.Ids.Denied,
        Event.State.Ids.Cancelled,
    ]);

    constructor() {
        void this.reload();
    }

    private toCriteriaType(typeName: BucketTypeName): SearchCriteriaType {
        return typeName === "Event Type" ? "eventTypes" : "eventStates";
    }

    private cloneColors(): ItemColor[] {
        return [...this.colors()];
    }

    private getAllowedItemIdsForCriteria(criteriaType: SearchCriteriaType): number[] | undefined {
        if (criteriaType !== "eventStates") {
            return undefined;
        }
        return this.allowedEventStateIds();
    }

    private getAllowedEventStateIds(eventStates: S25ItemI[]): number[] {
        return (eventStates ?? [])
            .filter((eventState) => !this.excludedEventStateIds.has(Number(eventState.itemId)))
            .map((eventState) => Number(eventState.itemId))
            .filter((eventStateId) => Number.isFinite(eventStateId));
    }

    private filterSelectedItems(criteriaType: SearchCriteriaType, selectedItems: S25ItemI[]): S25ItemI[] {
        if (criteriaType !== "eventStates") {
            return [...(selectedItems ?? [])];
        }
        return (selectedItems ?? []).filter(
            (selectedItem) => !this.excludedEventStateIds.has(Number(selectedItem.itemId)),
        );
    }

    selectStyle(bucket: ColorBucketVm): string {
        const backgroundColor = bucket.color?.hash;
        const textColor = bucket.color?.textColor;
        const patternCss = bucket.color?.pattern?.css?.trim();
        const normalizedPatternCss = patternCss ? patternCss.replace(/;+\s*$/, "") : "none";
        const styleParts = [
            backgroundColor ? `background-color: ${backgroundColor} !important` : "",
            textColor ? `color: ${textColor} !important` : "",
            `background-image: ${normalizedPatternCss} !important`,
        ];
        return styleParts.filter((stylePart) => !!stylePart).join("; ");
    }

    private normalizeBucket(
        rawBucket: {
            bucket_id: number;
            bucket_name: string;
            type: "Event Type" | "State";
            color: ItemColor;
            items: { itemId: number; itemName: string }[];
            is_new?: 0 | 1;
            is_removed?: boolean;
        },
        palette: ItemColor[],
    ): ColorBucketVm {
        const chosenColorName = rawBucket.color?.name;
        const mappedColor = chosenColorName
            ? (palette.find((colorOption) => colorOption.name === chosenColorName) ?? rawBucket.color)
            : undefined;
        const criteriaType = this.toCriteriaType(rawBucket.type as BucketTypeName);
        const mappedItems = (rawBucket.items ?? []).map((item) => ({
            itemId: Number(item.itemId),
            itemName: item.itemName,
        }));
        const selectedItems = this.filterSelectedItems(criteriaType, mappedItems);

        return {
            bucket_id: rawBucket.bucket_id,
            bucket_name: rawBucket.bucket_name ?? "",
            type: rawBucket.type as BucketTypeName,
            color: mappedColor,
            items: selectedItems,
            is_new: rawBucket.is_new,
            is_removed: rawBucket.is_removed,
            chosenColorName,
            colors: [...palette],
            selectedItems,
            criteriaType,
            allowedItemIds: this.getAllowedItemIdsForCriteria(criteriaType),
        };
    }

    private refreshBucketColors(currentType: ColorBucketTypeVm): void {
        const assignedColorNames = currentType.buckets
            .filter((bucket) => !bucket.is_removed)
            .map((bucket) => bucket.color?.name)
            .filter((colorName): colorName is string => !!colorName);

        for (const bucket of currentType.buckets) {
            bucket.colors = this.cloneColors().filter((colorOption) => {
                if (colorOption.name === bucket.color?.name) return true;
                return !assignedColorNames.includes(colorOption.name);
            });

            if (
                bucket.chosenColorName &&
                !bucket.colors.some((colorOption) => colorOption.name === bucket.chosenColorName)
            ) {
                bucket.color = undefined;
                bucket.chosenColorName = undefined;
            }
        }
    }

    private setBucketTypes(bucketTypes: ColorBucketType[]): void {
        const palette = this.cloneColors();
        const mappedTypes = (bucketTypes ?? []).map((bucketType) => {
            const mappedBuckets = (bucketType.buckets ?? []).map((rawBucket) =>
                this.normalizeBucket(rawBucket, palette),
            );
            const typedBucketType: ColorBucketTypeVm = {
                ...bucketType,
                type: bucketType.type as BucketTypeName,
                buckets: mappedBuckets,
                bucketCount: mappedBuckets.length,
            };
            this.refreshBucketColors(typedBucketType);
            return typedBucketType;
        });
        this.bucketTypes.set(mappedTypes);
    }

    private async reload(): Promise<void> {
        this.isInit.set(false);
        this.loading.set(true);
        this.validationMessage.set(undefined);

        try {
            const [bucketTypeResponse, colorSchemes, eventStates] = await Promise.all([
                ItemColorMappingService.getBucketTypes(),
                ItemColorMappingService.getColorSchemes(),
                SearchCriteriaService.getEventStates().catch(() => [] as S25ItemI[]),
            ]);

            this.colors.set([...(colorSchemes ?? [])]);
            this.allowedEventStateIds.set(this.getAllowedEventStateIds(eventStates));
            const mappedTypes = bucketTypeResponse?.types ?? [];
            this.setBucketTypes(mappedTypes);

            const enabledType = this.bucketTypes().find((bucketType) => bucketType.is_enabled === 1)?.type;
            if (enabledType) {
                this.colorBy.set(enabledType);
                this.colorByBool.set(enabledType === "State");
            }

            this.isInit.set(true);
        } finally {
            this.loading.set(false);
        }
    }

    enableType(selectedState: boolean): void {
        const selectedType: BucketTypeName = selectedState ? "State" : "Event Type";
        this.colorByBool.set(selectedState);
        this.colorBy.set(selectedType);
        this.bucketTypes.update((currentTypes) =>
            currentTypes.map((bucketType) => ({
                ...bucketType,
                is_enabled: bucketType.type === selectedType ? 1 : 0,
            })),
        );
    }

    addBucket(currentType: ColorBucketTypeVm): void {
        currentType.bucketCount += 1;
        const createdBucket: ColorBucketVm = {
            type: this.colorBy(),
            is_new: 1,
            colors: this.cloneColors(),
            items: [],
            selectedItems: [],
            criteriaType: this.toCriteriaType(this.colorBy()),
            allowedItemIds: this.getAllowedItemIdsForCriteria(this.toCriteriaType(this.colorBy())),
            bucket_name: "",
            bucket_id: undefined,
            color: undefined,
            chosenColorName: undefined,
        };
        currentType.buckets.push(createdBucket);
        this.refreshBucketColors(currentType);
        this.bucketTypes.update((types) => [...types]);
    }

    removeBucket(currentType: ColorBucketTypeVm, bucket: ColorBucketVm): void {
        currentType.bucketCount -= 1;
        if (bucket.is_new) {
            currentType.buckets = currentType.buckets.filter((candidate) => candidate !== bucket);
        } else {
            bucket.is_removed = true;
        }
        this.refreshBucketColors(currentType);
        this.bucketTypes.update((types) => [...types]);
    }

    pickColor(currentType: ColorBucketTypeVm, bucket: ColorBucketVm): void {
        const selected = this.colors().find((colorOption) => colorOption.name === bucket.chosenColorName);
        bucket.color = selected;
        this.refreshBucketColors(currentType);
        this.bucketTypes.update((types) => [...types]);
    }

    setSelectedItems(currentType: ColorBucketTypeVm, bucket: ColorBucketVm, selectedItems: S25ItemI[]): void {
        bucket.selectedItems = this.filterSelectedItems(bucket.criteriaType, selectedItems ?? []);
        bucket.items = bucket.selectedItems.map((selectedItem) => ({
            itemId: Number(selectedItem.itemId),
            itemName: selectedItem.itemName,
        }));

        const selectedIds = new Set(bucket.selectedItems.map((selectedItem) => Number(selectedItem.itemId)));
        for (const siblingBucket of currentType.buckets) {
            if (siblingBucket === bucket) continue;
            const filteredItems = (siblingBucket.selectedItems ?? []).filter(
                (selectedItem) => !selectedIds.has(Number(selectedItem.itemId)),
            );
            if (filteredItems.length === (siblingBucket.selectedItems ?? []).length) continue;

            siblingBucket.selectedItems = filteredItems;
            siblingBucket.items = filteredItems.map((selectedItem) => ({
                itemId: Number(selectedItem.itemId),
                itemName: selectedItem.itemName,
            }));
        }

        this.bucketTypes.update((types) => [...types]);
    }

    private validate(): string | true {
        const nameCounts: Record<string, number> = {};
        for (const bucketType of this.bucketTypes()) {
            for (const bucket of bucketType.buckets) {
                if (bucket.is_removed) continue;
                const normalizedName = bucket.bucket_name?.trim();
                if (!bucket.color) {
                    return "Please assign a color to each color mapping";
                }
                if (!normalizedName) {
                    return "Please assign a name to each color mapping";
                }
                if (!bucket.selectedItems?.length) {
                    return "Please assign items to each color mapping";
                }
                const nameKey = normalizedName.toLowerCase();
                nameCounts[nameKey] = (nameCounts[nameKey] ?? 0) + 1;
                if (nameCounts[nameKey] > 1) {
                    return "Please use a unique name for each color mapping";
                }
            }
        }
        return true;
    }

    private toSavePayload() {
        return {
            root: {
                types: this.bucketTypes().map((bucketType) => ({
                    is_enabled: bucketType.is_enabled,
                    type: bucketType.type,
                    buckets: bucketType.buckets.map((bucket) => {
                        const bucketPayload = {
                            bucket_id: bucket.bucket_id,
                            bucket_name: bucket.bucket_name,
                            type: bucket.type,
                            ...(bucket.is_removed ? { is_removed: true } : {}),
                            ...(bucket.is_new ? { is_new: true } : {}),
                            color: {
                                color_name: bucket.color?.name,
                                color_hash: bucket.color?.hash,
                            },
                            items: (bucket.selectedItems ?? []).map((selectedItem) => ({
                                item_id: selectedItem.itemId,
                            })),
                        };
                        return bucketPayload;
                    }),
                })),
            },
        };
    }

    async save(): Promise<void> {
        const validationResult = this.validate();
        if (validationResult !== true) {
            this.validationMessage.set(validationResult);
            return;
        }

        this.loading.set(true);
        try {
            const payload = this.toSavePayload();
            await ItemColorMappingService.putBucketTypes(payload as unknown as ColorBucketType[]);
            await ItemColorMappingService.loadCss();
            await this.reload();
            this.validationMessage.set("Success!");
        } catch (_error) {
            this.validationMessage.set("There was an error saving color mapping.");
            this.loading.set(false);
        }
    }
}
